import * as pkijs from 'pkijs/src'
import * as asn1js from 'asn1js'
import { getCrypto } from 'pkijs/src/common'
import { detect } from 'detect-browser'

import { engine } from './pki/PKIEngine'
import PKIUtils from './pki/PKIUtils'
import jwkToPem from 'jwk-to-pem'

const signAlgoMap = {
  RSA: 'RSASSA-PKCS1-V1_5',
  ECDSA: 'ECDSA'
}

export default class PKI {
  public async generatePrivateKeyAndCSR (signAlgo, hashAlgo, keyOption): Promise<any> {
    // console.log('generateKey():ENTER')
    const crypto = await getCrypto()
    const browser = detect()
    // hashAlgo: 'SHA-256', signAlgo: 'ECDSA', keyOption = { type: 'RSA', keySize: 2048 }
    const key = await engine.generateKey(signAlgoMap[signAlgo], hashAlgo, keyOption)
    let pkcs8
    if (browser && browser.name === 'firefox' && signAlgo === signAlgoMap.ECDSA) {
      // Export privateKey as JWK
      const jwk = await crypto.exportKey('jwk', key.privateKey)
      // Convert JWK to PEM
      const pem = jwkToPem(jwk, { private: true })
      // Remove PEM header
      const raw = await PKIUtils.removeHeader(pem)
      pkcs8 = PKIUtils.b642ary(raw)
    } else {
      pkcs8 = await crypto.exportKey('pkcs8', key.privateKey)
    }
    // console.log('App.generateKey():pkcs8=' + pkcs8)
    const pbytes = new Uint8Array(pkcs8)
    let pkcs8Pem = `-----BEGIN PRIVATE KEY-----\r\n`
    if (pbytes) {
      const keypem = PKIUtils.ary2b64(pbytes)
      pkcs8Pem = `${pkcs8Pem}${PKIUtils.fmt2pem(keypem)}`
      pkcs8Pem = `${pkcs8Pem}\r\n-----END PRIVATE KEY-----\r\n`
      // console.log('PKCS8:\n' + pkcs8Pem)
    }

    const csr = await engine.generateCSR(
      signAlgoMap[signAlgo],
      hashAlgo,
      key
    )

    return { pkcs8: pkcs8Pem, csr }
  }

  public async generatePKCS12 (pemCertsArr, pkcs8, password, pbeAlgo, macAlgo, schema): Promise<any> {
    // Decode Pem to Object
    const cleanCertsArr = pemCertsArr.map((cert: string): string => PKIUtils.removeHeader(cert))

    // console.log('App.generatePKCS12():certsArr= ', JSON.stringify(cleanCertsArr))

    const certObjects = cleanCertsArr.map((cert: string): string => {
      const certAsn1 = asn1js.fromBER(PKIUtils.b642ary(cert))
      return new pkijs.Certificate({ schema: certAsn1.result })
    })
    
    // Decode Pem to Object
    const keyPem = PKIUtils.removeHeader(pkcs8)
    // console.log('App.generatePKCS12():keypem=' + keyPem)
    const keyAsn1 = asn1js.fromBER(PKIUtils.b642ary(keyPem))
    const keyObj = new pkijs.PrivateKeyInfo({ schema: keyAsn1.result })
    // Generate PKCS#12
    const PBE_ALGO = pbeAlgo // and this one is static as well
    const MAC_ALGO = macAlgo // it is required, with SHA-256 we cannot import key+cert bundle into KeyChain in fact

    const p12Base64 = await engine.generatePKCS12(keyObj, certObjects, password, PBE_ALGO, MAC_ALGO, schema)
    // let temp = `-----BEGIN PKCS#12-----\r\n`
    // temp = `${temp}${PKIUtils.fmt2pem(p12Base64)}`
    // temp = `${temp}\r\n-----END PKCS#12-----\r\n`
    // console.log('PKCS12:\n' + temp)
    return p12Base64
  }
}
